import type { ITransaction } from 'oscar-typedef'
import { computed } from 'vue'
import { useI18nTypes } from '@/composables'
import { useWalletStore } from '@/stores'
import { Address, CoinAmount, type Maybe, type BaseAccount } from '@/types'

export class Transaction {
  id: string
  accountId: string
  accountTokenId?: string
  txid: string
  value: CoinAmount
  status: ITransaction['status']
  createDate: string
  to: Address
  type: ITransaction['type']

  account: Maybe<BaseAccount>

  constructor(transaction: ITransaction) {
    this.id = transaction.id
    this.accountId = transaction.accountId
    this.txid = transaction.txid
    this.status = transaction.status
    this.createDate = transaction.createDate
    this.to = new Address(transaction.to)
    this.type = transaction.type

    // V2: accountTokenId가 직접 필드로 있거나 tokenInfo에 있을 수 있음
    if ((transaction as any).accountTokenId) {
      this.accountTokenId = (transaction as any).accountTokenId
    } else if (transaction.tokenInfo) {
      this.accountTokenId = transaction.tokenInfo.accountTokenId
    }

    const walletStore = useWalletStore()
    const computedAccount = computed(() => {
      return this.accountTokenId
        ? walletStore.getAccountToken({ accountTokenId: this.accountTokenId })
        : walletStore.getAccount({ accountId: this.accountId })
    })
    Object.defineProperty(this, 'account', {
      get(): Maybe<BaseAccount> {
        return computedAccount.value
      },
    })

    // V2: decimals를 account에서 찾거나, V2 응답의 decimals 필드 사용
    const decimals =
      computedAccount.value?.coin?.decimals ??
      (transaction as any).decimals ??
      18
    this.value = new CoinAmount({
      amount: transaction.value,
      decimal: decimals,
    })
  }

  get isReceive() {
    return this.account?.address.equals(this.to)
  }
  get isSend() {
    return !this.account?.address.equals(this.to)
  }

  get ethUnitValue() {
    return this.value.ethValue.toFixed()
  }

  get exchangedValue() {
    return this.value.calculatePrice(this.account?.coin?.priceInfo).toFixed()
  }

  get iconStatus() {
    if (this.status === 'pending') {
      return 'pending'
    } else if (this.status === 'fail') {
      return 'cancel'
    } else if (this.isSend) {
      return 'sent'
    } else {
      return 'received'
    }
  }

  get statusText() {
    const { t } = useI18nTypes()
    if (this.status === 'pending') {
      return t('transaction_detail.status.pending')
    } else if (this.status === 'fail') {
      return t('transaction_detail.status.failed')
    } else if (this.status === 'confirm') {
      return t('transaction_detail.status.confirm')
    }
  }
}
